/**
 * 错误处理工具函数
 * 处理API返回的各种错误格式，确保不会渲染对象到React组件中
 */

export interface ErrorResponse {
  response?: {
    data?: any;
    status?: number;
  };
  message?: string;
}

/**
 * 解析API错误响应，返回用户友好的错误消息
 * 防止React错误#31（对象不能作为React子元素）
 */
export function parseErrorMessage(error: ErrorResponse, defaultMessage: string = '操作失败，请重试'): string {
  if (!error) return defaultMessage;

  // 优先检查响应数据
  if (error.response?.data) {
    const data = error.response.data;
    
    // 处理FastAPI的422验证错误（Pydantic验证错误）
    if (data.detail && Array.isArray(data.detail)) {
      return data.detail
        .map((err: any) => {
          // 提取有用的错误信息
          if (typeof err === 'string') return err;
          if (err.msg) return err.msg;
          if (err.message) return err.message;
          return '验证错误';
        })
        .join(', ');
    }
    
    // 处理字符串形式的详细错误
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    // 处理对象形式的错误（转换为字符串）
    if (typeof data.detail === 'object' && data.detail !== null) {
      // 尝试提取有用的错误信息
      if (data.detail.message) return data.detail.message;
      if (data.detail.error) return data.detail.error;
      // 最后兜底转换为JSON字符串
      return JSON.stringify(data.detail);
    }
    
    // 处理直接的字符串响应
    if (typeof data === 'string') {
      return data;
    }
    
    // 处理其他可能的错误格式
    if (data.message) return data.message;
    if (data.error) return data.error;
  }
  
  // 使用错误对象的message属性
  if (error.message && typeof error.message === 'string') {
    return error.message;
  }
  
  // 返回默认错误消息
  return defaultMessage;
}

/**
 * 格式化验证错误，提供更友好的中文提示
 */
export function formatValidationError(fieldError: any): string {
  if (typeof fieldError === 'string') return fieldError;
  
  if (fieldError.msg) {
    const msg = fieldError.msg;
    const field = fieldError.loc ? fieldError.loc[fieldError.loc.length - 1] : '';
    
    // 常见验证错误的中文化
    const messageMap: Record<string, string> = {
      'field required': `${field} 字段不能为空`,
      'ensure this value has at least': '长度不足',
      'ensure this value has at most': '长度超限',
      'value is not a valid email address': '邮箱格式不正确',
      'string does not match regex': '格式不正确',
    };
    
    for (const [key, value] of Object.entries(messageMap)) {
      if (msg.includes(key)) {
        return value;
      }
    }
    
    return msg;
  }
  
  return '验证错误';
}

/**
 * 检查错误状态码并返回相应的用户提示
 */
export function getStatusErrorMessage(status: number): string {
  const statusMap: Record<number, string> = {
    400: '请求参数错误',
    401: '未授权，请重新登录',
    403: '权限不足',
    404: '请求的资源不存在',
    422: '数据验证失败',
    500: '服务器内部错误',
    502: '网关错误',
    503: '服务暂时不可用',
    504: '请求超时',
  };
  
  return statusMap[status] || `请求失败 (${status})`;
}